import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldStar, Lock, Envelope } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import usePageMeta from "@/hooks/usePageMeta";

export default function Login() {
  usePageMeta(
    "Commander Sign In",
    "Sign in to your EIGOO Commander Account. Access your download vault, Sovereign Blueprints, and Ghost Crew deployments."
  );
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const from = location.state?.from || "/commander";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      navigate(typeof from === "string" ? from : "/commander");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-14 h-14 border border-[#FFD700] rotate-45 flex items-center justify-center mx-auto mb-6 animate-pulse-gold">
            <ShieldStar size={26} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <span className="overline">// Commander Access</span>
          <h1 className="heading-display text-4xl mt-3 mb-2">Resume Command</h1>
          <p className="text-sm text-white/50 font-light">Sign in to your provisioning fleet.</p>
        </div>

        <form onSubmit={submit} className="surface-card p-8 space-y-5">
          <div>
            <Label className="overline text-[0.6rem] mb-2 block">Commander Email</Label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                placeholder="commander@fleet.net"
              />
            </div>
          </div>
          <div>
            <Label className="overline text-[0.6rem] mb-2 block">Access Key</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
                placeholder="••••••••••••"
              />
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 p-3 font-mono text-xs">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full btn-gold py-4 text-xs disabled:opacity-50"
          >
            {loading ? "Authenticating..." : "Engage Bridge"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-white/50">
          New commander?{" "}
          <Link to="/register" className="text-[#FFD700] hover:underline font-semibold" data-testid="link-to-register">
            Commission an account
          </Link>
        </div>
      </div>
    </div>
  );
}
